import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

/**
 * Store Pickups — a live board of partner orders waiting to be collected at a store.
 *
 * An order lands here when the main backend matched its pickup address to one of that partner's
 * stores. Once the leg is collected it moves from the waiting list to "Picked up today", where it
 * stays (with the pickup time and driver) until midnight UAE - so ops can still see what has gone.
 * Nothing on this screen changes an order — it is a window, not a control.
 *
 * Auto-refresh is a plain interval against the driver backend. It deliberately does not blank the
 * table while refreshing: an ops board that flashes empty every few seconds is unreadable, so the
 * previous rows stay on screen until the new ones arrive.
 */
const PICKED_UP = "PICKED_UP";
const rowKey = (r) => (r && r.jobId ? `${r.stage === PICKED_UP ? "P" : "W"}:${r.jobId}` : null);

const StorePickups = () => {
  const API_URL = process.env.REACT_APP_API_URL;

  const [rows, setRows] = useState([]);
  const [stores, setStores] = useState([]);
  const [store, setStore] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  // Distinct from `loading`: `loading` covers the very first fetch (when there is nothing to show
  // yet), this covers a background poll over rows that are already on screen.
  const [refreshing, setRefreshing] = useState(false);
  // Job ids that appeared in the most recent poll, so an arriving order announces itself instead
  // of silently materialising in a list the operator is already reading.
  const [arrivedIds, setArrivedIds] = useState([]);
  const seenIdsRef = useRef(null);
  // Which row is expanded to full detail. One at a time: this is a wall board, and several open
  // panels push the waiting queue off the screen.
  const [openId, setOpenId] = useState(null);

  // Ticks once a second purely to re-render the "running since" clocks.
  //
  // The elapsed time is NOT recomputed from a timestamp sent by the server. The API reports
  // waitingMinutes measured by the DATABASE's clock, and this browser's clock can sit minutes or
  // whole timezones away from it; deriving the age here would reintroduce exactly the skew that
  // made every row read "0m" earlier. So the server's figure is the anchor and the browser only
  // adds the time that has passed on THIS page since the fetch landed - a quantity it can measure
  // correctly, because both ends of it are local.
  const [tick, setTick] = useState(0);
  const fetchedAtRef = useRef(Date.now());
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshSecs, setRefreshSecs] = useState(20);

  // Avoids a stale closure inside the interval, and lets a slow response be discarded if the
  // filter changed while it was in flight (otherwise the board can show the wrong store's work).
  const requestSeq = useRef(0);

  const load = useCallback(
    async (opts = {}) => {
      const seq = ++requestSeq.current;
      if (opts.showSpinner) setLoading(true);
      setRefreshing(true);
      try {
        const res = await axios.get(`${API_URL}shypvdriverapp/api/store-pickups`, {
          params: store ? { store, includeCollected: true } : { includeCollected: true },
        });
        if (seq !== requestSeq.current) return; // a newer request has already answered
        const data = res?.data;
        if (data && data.success === false) {
          const first = (data.errors || [])[0];
          setError(first?.errorDetails || first?.errorMessage || "Could not load the board");
        } else {
          const next = Array.isArray(data?.data) ? data.data : [];
          // First load establishes the baseline: everything is "new" then, which would flash the
          // whole table, so nothing is highlighted until there is a previous poll to compare with.
          // Keyed by stage as well, so a row moving from waiting to "picked up" flashes once there
          // instead of silently swapping sections.
          const previous = seenIdsRef.current;
          if (previous) {
            const fresh = next.map(rowKey).filter((k) => k && !previous.has(k));
            if (fresh.length) {
              setArrivedIds(fresh);
              // Clear the highlight after the animation so a row does not stay marked "new".
              setTimeout(() => setArrivedIds([]), 2600);
            }
          }
          fetchedAtRef.current = Date.now();
          seenIdsRef.current = new Set(next.map(rowKey));
          setRows(next);
          setError(null);
          setLastUpdated(new Date());
        }
      } catch (e) {
        if (seq !== requestSeq.current) return;
        setError(e?.message || "Could not reach the driver backend");
      } finally {
        if (seq === requestSeq.current) {
          setLoading(false);
          // Hold the pulse briefly so a fast response still reads as a refresh rather than a flicker.
          setTimeout(() => setRefreshing(false), 450);
        }
      }
    },
    [API_URL, store]
  );

  const loadStores = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}shypvdriverapp/api/store-pickups/stores`, {
        params: { includeCollected: true },
      });
      setStores(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (e) {
      // The filter is a convenience; losing it must not take the board down.
      setStores([]);
    }
  }, [API_URL]);

  useEffect(() => {
    load({ showSpinner: true });
    loadStores();
  }, [load, loadStores]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => {
      load();
      loadStores();
    }, Math.max(5, refreshSecs) * 1000);
    return () => clearInterval(id);
  }, [autoRefresh, refreshSecs, load, loadStores]);

  const ui = {
    container: { padding: "24px", backgroundColor: "#f5f7fa", minHeight: "100vh" },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "20px",
      flexWrap: "wrap",
      gap: "16px",
    },
    title: { fontSize: "22px", fontWeight: 600, color: "#2d3748", margin: 0 },
    subtitle: { fontSize: "13px", color: "#6b7280", marginTop: "4px" },
    card: {
      backgroundColor: "#fff",
      borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      border: "1px solid #e8ecf1",
      marginBottom: "20px",
    },
    cardHeader: {
      padding: "14px 20px",
      borderBottom: "1px solid #e8ecf1",
      backgroundColor: "#fafbfc",
      borderRadius: "12px 12px 0 0",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "12px",
    },
    label: {
      fontSize: "11px",
      fontWeight: 600,
      color: "#6b7280",
      textTransform: "uppercase",
      letterSpacing: "0.3px",
      marginRight: "8px",
    },
    select: {
      padding: "8px 12px",
      fontSize: "13px",
      border: "1px solid #d1d5db",
      borderRadius: "8px",
      backgroundColor: "#fff",
    },
    btn: {
      padding: "8px 18px",
      fontSize: "13px",
      fontWeight: 600,
      color: "#fff",
      backgroundColor: "#1e3a5f",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
    },
    badge: {
      display: "inline-block",
      padding: "5px 12px",
      borderRadius: "999px",
      fontSize: "12px",
      fontWeight: 600,
    },
    th: {
      textAlign: "left",
      padding: "10px 14px",
      borderBottom: "2px solid #e8ecf1",
      fontSize: "11px",
      color: "#6b7280",
      textTransform: "uppercase",
      letterSpacing: "0.3px",
      whiteSpace: "nowrap",
    },
    td: {
      padding: "12px 14px",
      borderBottom: "1px solid #eef1f5",
      fontSize: "13px",
      verticalAlign: "top",
    },
    empty: { padding: "56px 20px", textAlign: "center" },
  };

  // Waiting time is the one number that tells an operator something is going wrong, so it is the
  // only place this board uses colour.
  const ageStyle = (mins) => {
    if (mins == null) return { color: "#9ca3af" };
    if (mins >= 120) return { color: "#b42318", fontWeight: 700 };
    if (mins >= 45) return { color: "#8a5300", fontWeight: 600 };
    return { color: "#2f6b3f", fontWeight: 600 };
  };

  // Server-measured age, plus the seconds that have elapsed on this page since the fetch.
  // `tick` is read so this recomputes every second.
  const liveSeconds = (mins) => {
    if (mins == null) return null;
    void tick;
    return mins * 60 + Math.max(0, Math.floor((Date.now() - fetchedAtRef.current) / 1000));
  };

  const ageText = (mins) => {
    const total = liveSeconds(mins);
    if (total == null) return "—";
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const sec = total % 60;
    // Seconds only matter while the wait is short; past an hour they are noise.
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${String(sec).padStart(2, "0")}s`;
    return `${sec}s`;
  };

  /**
   * Where to open the pickup.
   *
   * Coordinates are used when the leg has them, because they drop the pin exactly where the
   * shipment is. Only about one leg in eight carries them, so the rest fall back to searching the
   * written address - which still lands the driver on the right street, and is far better than
   * hiding the action on most of the board.
   */
  const mapsUrl = (r) => {
    if (r.latitude != null && r.longitude != null) {
      return `https://www.google.com/maps/search/?api=1&query=${r.latitude},${r.longitude}`;
    }
    const q = [r.pickupAddress, r.city].filter(Boolean).join(", ");
    return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : null;
  };

  const directionsUrl = (r) => {
    if (r.latitude != null && r.longitude != null) {
      return `https://www.google.com/maps/dir/?api=1&destination=${r.latitude},${r.longitude}`;
    }
    const q = [r.pickupAddress, r.city].filter(Boolean).join(", ");
    return q ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(q)}` : null;
  };

  // A store-to-store job is one collection AND one delivery, both at partner stores; a
  // store-to-customer job ends at an ordinary address. The driver needs to know which before
  // setting off, so it is a pill rather than a buried field.
  const moveStyle = (t) =>
    t === "STORE_TO_STORE"
      ? { ...ui.badge, backgroundColor: "#ede9fe", color: "#5b21b6", fontSize: "10.5px" }
      : t === "TO_STORE"
      ? { ...ui.badge, backgroundColor: "#e0f2fe", color: "#075985", fontSize: "10.5px" }
      : { ...ui.badge, backgroundColor: "#e3efe6", color: "#2f6b3f", fontSize: "10.5px" };

  const statusStyle = (s) => {
    const v = String(s || "").toUpperCase();
    if (v === "PICKUP_ENROUTE") return { ...ui.badge, backgroundColor: "#e3edf9", color: "#1e3a5f" };
    if (v === "ASSIGNED") return { ...ui.badge, backgroundColor: "#e3efe6", color: "#2f6b3f" };
    return { ...ui.badge, backgroundColor: "#eef1f5", color: "#4a4f58" };
  };

  const waitingRows = rows.filter((r) => r.stage !== PICKED_UP);
  const pickedRows = rows.filter((r) => r.stage === PICKED_UP);
  const hhmm = (v) => (v ? String(v).replace("T", " ").slice(11, 16) : "—");

  return (
    <div style={ui.container}>
      {/* Motion is informational here, not decorative: the board refreshes itself, so it has to
          show that a refresh happened and which rows are new. Everything is kept under
          prefers-reduced-motion so it degrades to a plain colour change. */}
      <style>{`
        @keyframes sp-pulse { 0%,100% { opacity: 1 } 50% { opacity: .35 } }
        @keyframes sp-sweep { from { transform: translateX(-100%) } to { transform: translateX(400%) } }
        @keyframes sp-arrive {
          0%   { background-color: #fff7cc; }
          100% { background-color: transparent; }
        }
        .sp-refreshing { animation: sp-pulse 1s ease-in-out infinite; }
        .sp-bar { position: relative; overflow: hidden; height: 2px; background: #e8ecf1; }
        .sp-bar > i { position: absolute; inset: 0 auto 0 0; width: 25%; background: #1e3a5f; animation: sp-sweep 1.1s ease-in-out infinite; }
        .sp-arrived { animation: sp-arrive 2.4s ease-out; }
        @media (prefers-reduced-motion: reduce) {
          .sp-refreshing, .sp-bar > i, .sp-arrived { animation: none; }
          .sp-arrived { background-color: #fff7cc; }
        }
      `}</style>
      <div style={ui.header}>
        <div>
          <h1 style={ui.title}>Store Pickups</h1>
          <p style={ui.subtitle}>
            Partner orders waiting to be collected at a store. Once picked up, an order moves to
            "Picked up today" and stays there until midnight.
          </p>
        </div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ ...ui.badge, backgroundColor: "#1e3a5f", color: "#fff" }}>
            {waitingRows.length} waiting
          </span>
          <span style={{ ...ui.badge, backgroundColor: "#e3efe6", color: "#2f6b3f" }}>
            {pickedRows.length} picked up today
          </span>
          <span style={{ ...ui.badge, backgroundColor: "#eef1f5", color: "#4a4f58" }}>
            {stores.length} {stores.length === 1 ? "store" : "stores"}
          </span>
          {refreshing ? (
            <span className="sp-refreshing" style={{ fontSize: "12px", color: "#1e3a5f", fontWeight: 600 }}>
              refreshing…
            </span>
          ) : (
            lastUpdated && (
              <span style={{ fontSize: "12px", color: "#6b7280" }}>
                updated {lastUpdated.toLocaleTimeString()}
              </span>
            )
          )}
        </div>
      </div>

      <div style={ui.card}>
        <div style={ui.cardHeader}>
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
            <span style={ui.label}>Store</span>
            <select
              style={ui.select}
              value={store}
              onChange={(e) => setStore(e.target.value)}
            >
              <option value="">All stores</option>
              {stores.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <label style={{ fontSize: "13px", color: "#4a4f58", display: "flex", alignItems: "center", gap: "6px" }}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto refresh
            </label>
            <select
              style={ui.select}
              value={refreshSecs}
              onChange={(e) => setRefreshSecs(Number(e.target.value))}
              disabled={!autoRefresh}
            >
              <option value={10}>every 10s</option>
              <option value={20}>every 20s</option>
              <option value={60}>every 60s</option>
            </select>
            <button style={ui.btn} onClick={() => load({ showSpinner: true })}>
              Refresh now
            </button>
          </div>
        </div>

        {refreshing && <div className="sp-bar"><i /></div>}

        {error && (
          <div style={{ padding: "14px 20px", color: "#b42318", fontSize: "13px", backgroundColor: "#fdf3f2" }}>
            {error}
          </div>
        )}

        {loading && rows.length === 0 ? (
          <div style={ui.empty}>
            <p style={{ color: "#6b7280", fontSize: "14px", margin: 0 }}>Loading the board…</p>
          </div>
        ) : waitingRows.length === 0 ? (
          <div style={ui.empty}>
            <div style={{ fontSize: "28px", color: "#9ca3af", marginBottom: "8px" }}>—</div>
            <p style={{ color: "#374151", fontSize: "15px", margin: 0, fontWeight: 600 }}>
              Nothing waiting at a store
            </p>
            <p style={{ color: "#6b7280", fontSize: "13px", marginTop: "6px" }}>
              {store
                ? `No uncollected orders for ${store} right now.`
                : "New store orders appear here automatically as partners place them."}
            </p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: "#fafbfc" }}>
                  {["Store", "Move", "AWB / Ref", "Pickup from", "Contact", "Driver", "Status", "Running since", "Open"].map(
                    (h) => (
                      <th key={h} style={ui.th}>
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {waitingRows.map((r, i) => (
                  <React.Fragment key={`${r.jobId}-${i}`}>
                  <tr
                    className={arrivedIds.includes(rowKey(r)) ? "sp-arrived" : undefined}
                    onClick={() => setOpenId(openId === r.jobId ? null : r.jobId)}
                    style={{ cursor: "pointer", background: openId === r.jobId ? "#f5f8fc" : undefined }}
                    title="Click for full details"
                  >
                    <td style={ui.td}>
                      <span style={{ fontWeight: 600 }}>{r.store || "—"}</span>
                    </td>
                    <td style={{ ...ui.td, whiteSpace: "nowrap" }}>
                      <span style={moveStyle(r.movementType)}>
                        {r.movementLabel || "—"}
                      </span>
                      {r.destinationStore && (
                        <div style={{ fontSize: "10.5px", color: "#6b7280", marginTop: "3px" }}>
                          → {r.destinationStore}
                        </div>
                      )}
                    </td>
                    <td style={ui.td}>
                      <div style={{ fontWeight: 600 }}>{r.jobId || "—"}</div>
                      <div style={{ fontSize: "11px", color: "#6b7280" }}>
                        Ref: {r.businessRefNo || "—"}
                      </div>
                    </td>
                    <td style={{ ...ui.td, maxWidth: "280px" }}>{r.pickupAddress || "—"}</td>
                    <td style={ui.td}>
                      <div>{r.contactName || "—"}</div>
                      <div style={{ fontSize: "11px", color: "#6b7280" }}>{r.contactNumber || ""}</div>
                    </td>
                    <td style={ui.td}>
                      {r.driverName || r.driverId ? (
                        <>
                          <div>{r.driverName || "—"}</div>
                          <div style={{ fontSize: "11px", color: "#6b7280" }}>{r.driverId || ""}</div>
                        </>
                      ) : (
                        <span style={{ color: "#9ca3af" }}>Unassigned</span>
                      )}
                    </td>
                    <td style={ui.td}>
                      <span style={statusStyle(r.status)}>{r.status || "—"}</span>
                    </td>
                    <td style={{ ...ui.td, whiteSpace: "nowrap", ...ageStyle(r.waitingMinutes) }}>
                      {ageText(r.waitingMinutes)}
                    </td>
                    <td style={{ ...ui.td, whiteSpace: "nowrap" }}>
                      {mapsUrl(r) ? (
                        <a
                          href={mapsUrl(r)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{ color: "#1e3a5f", fontWeight: 600, textDecoration: "none", fontSize: "12px" }}
                          title={r.latitude != null ? "Exact pickup coordinates" : "Searches the written address — this leg has no coordinates"}
                        >
                          Map{r.latitude == null ? " (approx)" : ""}
                        </a>
                      ) : (
                        <span style={{ color: "#9ca3af", fontSize: "12px" }}>—</span>
                      )}
                    </td>
                  </tr>
                  {openId === r.jobId && (
                    <tr>
                      <td colSpan={9} style={{ padding: "0 14px 16px", borderBottom: "1px solid #eef1f5", background: "#f5f8fc" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "14px", padding: "14px 0" }}>
                          {[
                            ["Store", r.store],
                            ["Move", r.movementLabel],
                            ["Destination store", r.destinationStore],
                            ["Destination address", r.destinationAddress],
                            ["Delivery leg AWB", r.siblingJobId],
                            ["AWB", r.jobId],
                            ["Business reference", r.businessRefNo],
                            ["Status", r.status],
                            ["Pickup address", r.pickupAddress],
                            ["City", r.city],
                            ["Contact", [r.contactName, r.contactNumber].filter(Boolean).join(" · ")],
                            ["Driver", r.driverName ? `${r.driverName}${r.driverId ? ` (${r.driverId})` : ""}` : "Unassigned"],
                            ["Order date", r.orderDate],
                            ["Scanned", r.scanDateTime ? String(r.scanDateTime).replace("T", " ") : null],
                            [
                              "Coordinates",
                              r.latitude != null && r.longitude != null
                                ? `${r.latitude}, ${r.longitude}`
                                : "not geocoded — map falls back to the address",
                            ],
                            ["Waiting", ageText(r.waitingMinutes)],
                          ].map(([label, value]) => (
                            <div key={label}>
                              <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.3px", color: "#6b7280", marginBottom: "2px" }}>
                                {label}
                              </div>
                              <div style={{ fontSize: "13px", color: "#111827", wordBreak: "break-word" }}>
                                {value || <span style={{ color: "#9ca3af" }}>—</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", paddingBottom: "4px" }}>
                          {mapsUrl(r) && (
                            <a href={mapsUrl(r)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                               style={{ ...ui.btn, textDecoration: "none", display: "inline-block" }}>
                              Open pickup location
                            </a>
                          )}
                          {directionsUrl(r) && (
                            <a href={directionsUrl(r)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                               style={{ ...ui.btn, background: "#fff", color: "#1e3a5f", border: "1px solid #1e3a5f", textDecoration: "none", display: "inline-block" }}>
                              Directions
                            </a>
                          )}
                          {r.contactNumber && (
                            <a href={`tel:${r.contactNumber}`} onClick={(e) => e.stopPropagation()}
                               style={{ ...ui.btn, background: "#fff", color: "#1e3a5f", border: "1px solid #cbd5e1", textDecoration: "none", display: "inline-block" }}>
                              Call {r.contactName || "pickup"}
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pickedRows.length > 0 && (
        <div style={ui.card}>
          <div style={ui.cardHeader}>
            <span style={{ fontSize: "14px", fontWeight: 700, color: "#2f6b3f" }}>
              Picked up today ({pickedRows.length})
            </span>
            <span style={{ fontSize: "12px", color: "#6b7280" }}>
              Collected from a store today — clears at midnight
            </span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: "#fafbfc" }}>
                  {["Store", "Move", "AWB / Ref", "Pickup from", "Driver", "Picked up", "Waited", "Status"].map((h) => (
                    <th key={h} style={ui.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pickedRows.map((r, i) => (
                  <tr key={`${r.jobId}-p-${i}`} className={arrivedIds.includes(rowKey(r)) ? "sp-arrived" : undefined}>
                    <td style={ui.td}><span style={{ fontWeight: 600 }}>{r.store || "—"}</span></td>
                    <td style={{ ...ui.td, whiteSpace: "nowrap" }}>
                      <span style={moveStyle(r.movementType)}>{r.movementLabel || "—"}</span>
                      {r.destinationStore && (
                        <div style={{ fontSize: "10.5px", color: "#6b7280", marginTop: "3px" }}>→ {r.destinationStore}</div>
                      )}
                    </td>
                    <td style={ui.td}>
                      <div style={{ fontWeight: 600 }}>{r.jobId || "—"}</div>
                      <div style={{ fontSize: "11px", color: "#6b7280" }}>Ref: {r.businessRefNo || "—"}</div>
                    </td>
                    <td style={{ ...ui.td, maxWidth: "280px" }}>{r.pickupAddress || "—"}</td>
                    <td style={ui.td}>
                      <div>{r.driverName || "—"}</div>
                      <div style={{ fontSize: "11px", color: "#6b7280" }}>{r.driverId || ""}</div>
                    </td>
                    <td style={{ ...ui.td, whiteSpace: "nowrap" }}>
                      <span style={{ ...ui.badge, backgroundColor: "#e3efe6", color: "#2f6b3f", fontSize: "11px" }}>
                        Picked up {hhmm(r.pickedUpAt)}
                      </span>
                      {r.pickedMinutesAgo != null && (
                        <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "3px" }}>{ageText(r.pickedMinutesAgo)} ago</div>
                      )}
                    </td>
                    <td style={{ ...ui.td, whiteSpace: "nowrap", color: "#4a4f58" }}>
                      {r.waitingMinutes != null ? `${Math.floor(r.waitingMinutes / 60) > 0 ? `${Math.floor(r.waitingMinutes / 60)}h ` : ""}${r.waitingMinutes % 60}m` : "—"}
                    </td>
                    <td style={ui.td}><span style={statusStyle(r.status)}>{r.status || "—"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default StorePickups;
